import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Stack,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Paper,
  Divider,
} from "@mui/material";
import {
  DataGrid,
  useGridApiRef,
  GridCellEditStartParams,
  GridCellParams,
} from "@mui/x-data-grid";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { ParamExUr } from "./types";
import { normalizeParamExUrList } from "../utils/paramExUrTemplate";

// API Configuration
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";
const API_ENDPOINTS = {
  consultations: "/Consultatii",
  paramExUrEmpty: "/Consultatii/ParamExUr/Empty",
};

const resolveApiUrl = (baseUrl: string, pathOrUrl: string) => {
  const value = (pathOrUrl || "").trim();
  if (!value) return baseUrl;
  if (/^https?:\/\//i.test(value)) return value;
  const normalizedBase = baseUrl.replace(/\/+$/, "");
  const normalizedPath = value.startsWith("/") ? value : `/${value}`;
  return `${normalizedBase}${normalizedPath}`;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`consultation-tabpanel-${index}`}
      aria-labelledby={`consultation-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

type ConsultationPayload = {
  IdCons?: number;
  IdPersoana?: number;
  Data?: string;
  Motivele_prezentarii?: string;
  Diagnostic?: string;
  Antecedente?: string;
  Anamneza?: string;
  Factor_risc?: string;
  FactoriDeRisc?: string;
  Examen_clinic?: string;
  ExamenClinic?: string;
  Examen_laborator?: string;
  ExamenLaborator?: string;
  Ecografie?: string;
  DopplerVenos?: string;
  DopplerArterial?: string;
  DopplerCarotidian?: string;
  Cistoscopie?: string;
  HolterTA?: string;
  EKG?: string;
  HolterECG?: string;
  TratamentEfectuat?: string;
  TratamentRecomandat?: string;
  Reteta?: string;
  Medicatii?: any;
  AlteInformatii?: string;
  Status?: string;
  Durata_min?: number;
  Medic?: string;
  TestEfort?: string;
  paramExUrList?: ParamExUr[];
};

export default function ConsultationForm({
  initial = {},
  onSaved,
}: {
  initial?: any;
  onSaved?: () => void;
}) {
  console.log(
    "[ConsultationForm] Component initialized with initial data:",
    initial,
  );
  console.log("[ConsultationForm] Initial data keys:", Object.keys(initial));
  console.log(
    "[ConsultationForm] Initial ParamExUr:",
    initial.ParamExUr ?? initial.paramExUr,
  );

  const [tabValue, setTabValue] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const paramExUrApiRef = useGridApiRef();

  const [form, setForm] = useState<ConsultationPayload>({
    IdCons: initial.IdCons ?? initial.idCons ?? undefined,
    IdPersoana: initial.IdPersoana ?? initial.idPersoana ?? undefined,
    Data: initial.Data ?? initial.data ?? new Date().toISOString().slice(0, 16),
    Motivele_prezentarii:
      initial.Motivele_prezentarii ?? initial.motivele_prezentarii ?? "",
    Diagnostic: initial.Diagnostic ?? initial.diagnostic ?? "",
    Antecedente: initial.Antecedente ?? initial.antecedente ?? "",
    Anamneza: initial.Anamneza ?? initial.anamneza ?? "",
    Factor_risc: initial.Factor_risc ?? initial.factor_risc ?? "",
    FactoriDeRisc: initial.FactoriDeRisc ?? initial.factoriDeRisc ?? "",
    Examen_clinic:
      initial.Examen_clinic ??
      initial.examenClinic ??
      initial.examen_clinic ??
      "",
    ExamenClinic: initial.ExamenClinic ?? initial.examenClinic ?? "",
    Examen_laborator:
      initial.Examen_laborator ??
      initial.examenLaborator ??
      initial.examen_laborator ??
      "",
    ExamenLaborator: initial.ExamenLaborator ?? initial.examenLaborator ?? "",
    Ecografie: initial.Ecografie ?? initial.ecografie ?? "",
    DopplerVenos: initial.DopplerVenos ?? initial.dopplerVenos ?? "",
    DopplerArterial: initial.DopplerArterial ?? initial.dopplerArterial ?? "",
    DopplerCarotidian:
      initial.DopplerCarotidian ?? initial.dopplerCarotidian ?? "",
    Cistoscopie: initial.Cistoscopie ?? initial.cistoscopie ?? "",
    HolterTA: initial.HolterTA ?? initial.holterTA ?? "",
    EKG: initial.EKG ?? initial.ekg ?? "",
    HolterECG: initial.HolterECG ?? initial.holterECG ?? "",
    TratamentEfectuat:
      initial.TratamentEfectuat ?? initial.tratamentEfectuat ?? "",
    TratamentRecomandat:
      initial.TratamentRecomandat ?? initial.tratamentRecomandat ?? "",
    Reteta: initial.Reteta ?? initial.reteta ?? "",
    Medicatii: initial.Medicatii ?? initial.medicatii ?? [],
    AlteInformatii: initial.AlteInformatii ?? initial.alteInformatii ?? "",
    Status: initial.Status ?? initial.status ?? "",
    Durata_min: initial.Durata_min ?? initial.durata_min ?? undefined,
    Medic: initial.Medic ?? initial.medic ?? "",
    TestEfort: initial.TestEfort ?? initial.testEfort ?? "",
    paramExUrList: normalizeParamExUrList(
      initial.ParamExUrList ?? initial.paramExUrList ?? [],
    ),
  });

  const isNewConsultation = useMemo(() => {
    const existingId =
      initial?.idCons ?? initial?.IdCons ?? initial?.id ?? form.IdCons;
    return existingId === undefined || existingId === null || existingId === "";
  }, [initial, form.IdCons]);

  useEffect(() => {
    const currentCount = form.paramExUrList?.length ?? 0;
    if (!isNewConsultation) return;
    if (currentCount > 0) return;

    const configured =
      process.env.REACT_APP_CONSULTATION_PARAM_EXUR_EMPTY_ENDPOINT ||
      API_ENDPOINTS.paramExUrEmpty;
    const url = resolveApiUrl(API_BASE_URL, configured);

    const controller = new AbortController();

    (async () => {
      console.log("[ConsultationForm] Loading empty ParamExUr list", {
        url,
        configured,
      });

      try {
        const response = await fetch(url, {
          method: "GET",
          signal: controller.signal,
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-API-Key": process.env.REACT_APP_API_KEY || "",
          },
        });

        if (!response.ok) {
          const text = await response.text().catch(() => "");
          console.warn(
            "[ConsultationForm] Empty ParamExUr request failed",
            response.status,
            text,
          );
          return;
        }

        const data: any = await response.json().catch(() => null);
        const listCandidate =
          (Array.isArray(data) && data) ||
          data?.ParamExUrList ||
          data?.paramExUrList ||
          data?.ParamExUr ||
          data?.paramExUr ||
          [];

        if (!Array.isArray(listCandidate)) {
          console.warn(
            "[ConsultationForm] Empty ParamExUr response is not an array",
            data,
          );
          return;
        }

        const normalized = normalizeParamExUrList(listCandidate);

        console.log("[ConsultationForm] Empty ParamExUr loaded", {
          count: normalized.length,
        });

        setForm((s) => ({ ...s, paramExUrList: normalized }));
      } catch (err: any) {
        if (err?.name === "AbortError") return;
        console.error("[ConsultationForm] Empty ParamExUr load error", err);
      }
    })();

    return () => controller.abort();
  }, [form.paramExUrList?.length, isNewConsultation, initial]);

  console.log("[ConsultationForm] Form state initialized:", form);
  console.log("[ConsultationForm] Form has IdCons:", form.IdCons);
  console.log("[ConsultationForm] Form has Diagnostic:", form.Diagnostic);
  console.log(
    "[ConsultationForm] Form paramExUrList count:",
    form.paramExUrList?.length ?? 0,
  );

  const STRING_LIMITS: Partial<Record<keyof ConsultationPayload, number>> = {
    Motivele_prezentarii: 2000,
    Diagnostic: 4000,
    Anamneza: 4000,
    Antecedente: 4000,
    Factor_risc: 2000,
    FactoriDeRisc: 2000,
    Examen_clinic: 8000,
    ExamenClinic: 8000,
    Examen_laborator: 8000,
    ExamenLaborator: 8000,
    Ecografie: 4000,
    DopplerVenos: 4000,
    DopplerArterial: 4000,
    DopplerCarotidian: 4000,
    Cistoscopie: 4000,
    HolterTA: 2000,
    EKG: 2000,
    HolterECG: 2000,
    TratamentEfectuat: 8000,
    TratamentRecomandat: 8000,
    Reteta: 8000,
    AlteInformatii: 4000,
    Status: 255,
    Medic: 255,
    TestEfort: 1000,
  };

  const PARAM_EX_UR_LIMITS = {
    denumire: 100,
    valoare: 200,
  } as const;

  const getTrimmedLength = (value: unknown) => {
    if (typeof value !== "string") return 0;
    return value.trim().length;
  };

  const setFieldError = (key: string, message?: string) => {
    setFieldErrors((prev) => {
      if (!message) {
        if (!(key in prev)) return prev;
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      if (prev[key] === message) return prev;
      return { ...prev, [key]: message };
    });
  };

  const validateStringField = (
    key: keyof ConsultationPayload,
    label: string,
    value: unknown,
  ) => {
    const maxLen = STRING_LIMITS[key];
    if (!maxLen) return;
    const len = getTrimmedLength(value);
    if (len > maxLen) {
      setFieldError(
        String(key),
        `${label} depășește lungimea maximă (${len}/${maxLen}).`,
      );
    } else {
      setFieldError(String(key));
    }
  };

  const validateBeforeSave = (payload: ConsultationPayload) => {
    const nextErrors: Record<string, string> = {};

    (Object.keys(STRING_LIMITS) as Array<keyof ConsultationPayload>).forEach(
      (key) => {
        const maxLen = STRING_LIMITS[key];
        if (!maxLen) return;
        const value = payload[key];
        const len = getTrimmedLength(value);
        if (len > maxLen) {
          nextErrors[String(key)] =
            `Depășește lungimea maximă (${len}/${maxLen}).`;
        }
      },
    );

    (payload.paramExUrList || []).forEach((param, index) => {
      const nameLen = getTrimmedLength(param.denumire);
      if (nameLen > PARAM_EX_UR_LIMITS.denumire) {
        nextErrors[`paramExUrList[${index}].denumire`] =
          `Parametru (Denumire) depășește lungimea maximă (${nameLen}/${PARAM_EX_UR_LIMITS.denumire}).`;
      }
      const valueLen = getTrimmedLength(param.valoare);
      if (valueLen > PARAM_EX_UR_LIMITS.valoare) {
        nextErrors[`paramExUrList[${index}].valoare`] =
          `Parametru (Valoare) depășește lungimea maximă (${valueLen}/${PARAM_EX_UR_LIMITS.valoare}).`;
      }
    });

    return nextErrors;
  };

  function updateField<K extends keyof ConsultationPayload>(
    k: K,
    v: ConsultationPayload[K],
  ) {
    console.log(`[ConsultationForm] Updating field "${String(k)}":`, v);
    // Clear/save-time errors as user edits
    if (typeof v === "string") {
      validateStringField(k, String(k), v);
    } else {
      setFieldError(String(k));
    }
    const nextValue =
      k === "paramExUrList"
        ? (normalizeParamExUrList(v as unknown) as ConsultationPayload[K])
        : v;

    setForm((s) => {
      const newState = { ...s, [k]: nextValue };
      console.log(
        `[ConsultationForm] New form state after updating "${String(k)}":`,
        newState,
      );
      return newState;
    });
  }

  const scrollParamExUrCellIntoView = (id: any, field?: string) => {
    try {
      const api: any = paramExUrApiRef.current;
      if (!api) return;

      const rowIndex: number | null =
        typeof api.getRowIndexRelativeToVisibleRows === "function"
          ? api.getRowIndexRelativeToVisibleRows(id)
          : ((api.getRow?.(id) as any)?.rowIndex ?? null);

      if (rowIndex === null || rowIndex === undefined) return;

      let colIndex = 0;
      if (field && typeof api.getAllColumns === "function") {
        const cols = api.getAllColumns();
        const idx = cols.findIndex((c: any) => c?.field === field);
        if (idx >= 0) colIndex = idx;
      }

      if (typeof api.scrollToIndexes === "function") {
        api.scrollToIndexes({ rowIndex, colIndex });
      }
    } catch {
      // best-effort scrolling; ignore
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    console.log(
      `[ConsultationForm] Tab changed from ${tabValue} to ${newValue}`,
    );
    const tabNames = [
      "Consult extern",
      "Alte informatii",
      "Ecocardiografie",
      "Examene Paraclinice",
      "Parametri Examen Urina",
      "Tratament",
      "Reteta",
    ];
    console.log(`[ConsultationForm] Switching to tab: ${tabNames[newValue]}`);
    setTabValue(newValue);
  };

  const deleteParamExUr = (index: number) => {
    console.log(`[ConsultationForm] Deleting ParamExUr at index ${index}`);
    const currentParams = form.paramExUrList || [];
    const deletedParam = currentParams[index];
    console.log("[ConsultationForm] Deleted ParamExUr:", deletedParam);

    const newParams = currentParams.filter((_, i) => i !== index);
    // Reorder positions
    const reorderedParams = newParams.map((param, idx) => ({
      ...param,
      pozitie: idx + 1,
    }));

    console.log(
      "[ConsultationForm] Remaining ParamExUr count:",
      reorderedParams.length,
    );
    updateField("paramExUrList", reorderedParams);
  };

  async function save() {
    console.log("[ConsultationForm] Save initiated");
    console.log("[ConsultationForm] Current form state:", form);
    console.log("[ConsultationForm] Initial data:", initial);
    console.log(
      "[ConsultationForm] ParamExUr to save:",
      JSON.stringify(form.paramExUrList, null, 2),
    );

    const validationErrors = validateBeforeSave(form);
    if (Object.keys(validationErrors).length > 0) {
      console.warn(
        "[ConsultationForm] Validation failed:",
        JSON.stringify(validationErrors, null, 2),
      );
      setFieldErrors(validationErrors);
      throw new Error("Validation failed");
    }

    const method = initial && initial.idCons ? "PUT" : "POST";
    const url =
      initial && initial.idCons
        ? `${API_BASE_URL}${API_ENDPOINTS.consultations}/${initial.idCons}`
        : `${API_BASE_URL}${API_ENDPOINTS.consultations}`;

    // Populate Medic from logged-in user (stored by LoginPage)
    const resolveLoggedInUserName = (): string => {
      try {
        const raw = localStorage.getItem("user");
        if (!raw) return "";
        const u = JSON.parse(raw);
        return String(
          u?.name || u?.displayName || u?.username || u?.email || "",
        ).trim();
      } catch {
        return "";
      }
    };
    const loggedInMedicName = resolveLoggedInUserName();
    const payload: ConsultationPayload = {
      ...form,
      Medic: loggedInMedicName || form.Medic,
    };

    console.log(
      `[ConsultationForm] Saving with method: ${method}, URL: ${url}`,
    );
    console.log(
      "[ConsultationForm] Payload:",
      JSON.stringify(payload, null, 2),
    );

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "X-API-Key": process.env.REACT_APP_API_KEY || "",
        },
        body: JSON.stringify(payload),
      });

      console.log(`[ConsultationForm] Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[ConsultationForm] Save failed:", errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json().catch(() => null);
      console.log("[ConsultationForm] Save successful, result:", result);
      console.log(
        "[ConsultationForm] Saved ParamExUr:",
        result?.ParamExUr ?? result?.paramExUr,
      );

      onSaved && onSaved();
      console.log("[ConsultationForm] onSaved callback executed");
    } catch (error) {
      console.error("[ConsultationForm] Save error:", error);
      throw error;
    }
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="consultation form tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Consult extern" />
          <Tab label="Alte informatii" />
          <Tab label="Ecocardiografie" />
          <Tab label="Examene Paraclinice" />
          <Tab label="Tratament" />
          <Tab label="Reteta" />
        </Tabs>
      </Box>

      {/* Tab 0: General Information */}
      <TabPanel value={tabValue} index={0}>
        <Stack spacing={2}>
          <TextField
            label="Motivul prezentării"
            value={form.Motivele_prezentarii}
            onChange={(e) =>
              updateField("Motivele_prezentarii", e.target.value)
            }
            onBlur={(e) =>
              validateStringField(
                "Motivele_prezentarii",
                "Motivul prezentării",
                e.target.value,
              )
            }
            error={Boolean(fieldErrors.Motivele_prezentarii)}
            helperText={fieldErrors.Motivele_prezentarii}
            inputProps={{ maxLength: STRING_LIMITS.Motivele_prezentarii }}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="Diagnostic"
            value={form.Diagnostic}
            onChange={(e) => updateField("Diagnostic", e.target.value)}
            onBlur={(e) =>
              validateStringField("Diagnostic", "Diagnostic", e.target.value)
            }
            error={Boolean(fieldErrors.Diagnostic)}
            helperText={fieldErrors.Diagnostic}
            inputProps={{ maxLength: STRING_LIMITS.Diagnostic }}
            multiline
            rows={2}
            fullWidth
          />
          <TextField
            label="Anamneza"
            value={form.Anamneza}
            onChange={(e) => updateField("Anamneza", e.target.value)}
            onBlur={(e) =>
              validateStringField("Anamneza", "Anamneza", e.target.value)
            }
            error={Boolean(fieldErrors.Anamneza)}
            helperText={fieldErrors.Anamneza}
            inputProps={{ maxLength: STRING_LIMITS.Anamneza }}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="Examen clinic"
            value={form.ExamenClinic || form.Examen_clinic}
            onChange={(e) => updateField("ExamenClinic", e.target.value)}
            onBlur={(e) =>
              validateStringField(
                "ExamenClinic",
                "Examen clinic",
                e.target.value,
              )
            }
            error={Boolean(fieldErrors.ExamenClinic)}
            helperText={fieldErrors.ExamenClinic}
            inputProps={{ maxLength: STRING_LIMITS.ExamenClinic }}
            multiline
            rows={4}
            fullWidth
          />
          <TextField
            label="Examen laborator"
            value={form.ExamenLaborator || form.Examen_laborator}
            onChange={(e) => updateField("ExamenLaborator", e.target.value)}
            onBlur={(e) =>
              validateStringField(
                "ExamenLaborator",
                "Examen laborator",
                e.target.value,
              )
            }
            error={Boolean(fieldErrors.ExamenLaborator)}
            helperText={fieldErrors.ExamenLaborator}
            inputProps={{ maxLength: STRING_LIMITS.ExamenLaborator }}
            multiline
            rows={4}
            fullWidth
          />
        </Stack>
      </TabPanel>

      {/* Tab 1: Alte Informatii */}
      <TabPanel value={tabValue} index={1}>
        <Stack spacing={2}>
          <TextField
            label="Antecedente"
            value={form.Antecedente}
            onChange={(e) => updateField("Antecedente", e.target.value)}
            onBlur={(e) =>
              validateStringField("Antecedente", "Antecedente", e.target.value)
            }
            error={Boolean(fieldErrors.Antecedente)}
            helperText={fieldErrors.Antecedente}
            inputProps={{ maxLength: STRING_LIMITS.Antecedente }}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="Factori de risc"
            value={form.FactoriDeRisc || form.Factor_risc}
            onChange={(e) => updateField("FactoriDeRisc", e.target.value)}
            onBlur={(e) =>
              validateStringField(
                "FactoriDeRisc",
                "Factori de risc",
                e.target.value,
              )
            }
            error={Boolean(fieldErrors.FactoriDeRisc)}
            helperText={fieldErrors.FactoriDeRisc}
            inputProps={{ maxLength: STRING_LIMITS.FactoriDeRisc }}
            multiline
            rows={2}
            fullWidth
          />
          <TextField
            label="Alte informații"
            value={form.AlteInformatii}
            onChange={(e) => updateField("AlteInformatii", e.target.value)}
            onBlur={(e) =>
              validateStringField(
                "AlteInformatii",
                "Alte informații",
                e.target.value,
              )
            }
            error={Boolean(fieldErrors.AlteInformatii)}
            helperText={fieldErrors.AlteInformatii}
            inputProps={{ maxLength: STRING_LIMITS.AlteInformatii }}
            multiline
            rows={3}
            fullWidth
          />
        </Stack>
      </TabPanel>

      {/* ✅ Tab 2: Parametri Examen Urina */}
      <TabPanel value={tabValue} index={2}>
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            {/* 
            <Typography variant="h6" color="primary">
              🔬 Parametri Examen Urină
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addParamExUr}
              size="small"
            >
              Adaugă Parametru
            </Button>
            */}
          </Box>

          <Paper sx={{ height: 600, width: "100%" }}>
            <DataGrid
              apiRef={paramExUrApiRef}
              rows={(form.paramExUrList || []).map((param, index) => ({
                id: param.idParam || `tmp-${index}`,
                ...param,
                rowIndex: index,
              }))}
              rowHeight={32}
              columns={[
                {
                  field: "pozitie",
                  headerName: "Nr.",
                  width: 70,
                  editable: false,
                  align: "center",
                  headerAlign: "center",
                },
                {
                  field: "denumire",
                  headerName: "Denumire Parametru",
                  width: 250,
                  editable: true,
                },
                {
                  field: "valoare",
                  headerName: "Valoare",
                  width: 400,
                  editable: true,
                },
                {
                  field: "actions",
                  headerName: "Acțiuni",
                  width: 100,
                  sortable: false,
                  filterable: false,
                  renderCell: (params) => (
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => deleteParamExUr(params.row.rowIndex)}
                      title="Șterge"
                    >
                      <DeleteIcon />
                    </IconButton>
                  ),
                },
              ]}
              processRowUpdate={(newRow, oldRow) => {
                const denumireLen = getTrimmedLength(newRow.denumire);
                if (denumireLen > PARAM_EX_UR_LIMITS.denumire) {
                  throw new Error(
                    `Denumire prea lungă (${denumireLen}/${PARAM_EX_UR_LIMITS.denumire}).`,
                  );
                }
                const valoareLen = getTrimmedLength(newRow.valoare);
                if (valoareLen > PARAM_EX_UR_LIMITS.valoare) {
                  throw new Error(
                    `Valoare prea lungă (${valoareLen}/${PARAM_EX_UR_LIMITS.valoare}).`,
                  );
                }

                const rowIndex = Number(oldRow?.rowIndex);
                if (Number.isFinite(rowIndex) && rowIndex >= 0) {
                  const updated = [...(form.paramExUrList || [])];
                  if (rowIndex < updated.length) {
                    updated[rowIndex] = {
                      ...updated[rowIndex],
                      denumire: newRow.denumire,
                      valoare: newRow.valoare,
                    };
                    updateField("paramExUrList", updated);
                  }
                }
                return newRow;
              }}
              onCellEditStart={(params: GridCellEditStartParams) => {
                scrollParamExUrCellIntoView(params.id, params.field);
              }}
              onCellClick={(params: GridCellParams) => {
                scrollParamExUrCellIntoView(params.id, params.field);
              }}
              onProcessRowUpdateError={(error) => {
                console.error("[ConsultationForm] Row update error:", error);
              }}
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              disableRowSelectionOnClick
              sx={{
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                },
              }}
            />
          </Paper>
        </Box>
      </TabPanel>

      {/* Tab 3: Paraclinical Exams */}
      <TabPanel value={tabValue} index={3}>
        <Stack spacing={2}>
          <TextField
            label="Doppler Venos"
            value={form.DopplerVenos}
            onChange={(e) => updateField("DopplerVenos", e.target.value)}
            onBlur={(e) =>
              validateStringField(
                "DopplerVenos",
                "Doppler Venos",
                e.target.value,
              )
            }
            error={Boolean(fieldErrors.DopplerVenos)}
            helperText={fieldErrors.DopplerVenos}
            inputProps={{ maxLength: STRING_LIMITS.DopplerVenos }}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="Doppler Arterial"
            value={form.DopplerArterial}
            onChange={(e) => updateField("DopplerArterial", e.target.value)}
            onBlur={(e) =>
              validateStringField(
                "DopplerArterial",
                "Doppler Arterial",
                e.target.value,
              )
            }
            error={Boolean(fieldErrors.DopplerArterial)}
            helperText={fieldErrors.DopplerArterial}
            inputProps={{ maxLength: STRING_LIMITS.DopplerArterial }}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="Doppler Carotidian"
            value={form.DopplerCarotidian}
            onChange={(e) => updateField("DopplerCarotidian", e.target.value)}
            onBlur={(e) =>
              validateStringField(
                "DopplerCarotidian",
                "Doppler Carotidian",
                e.target.value,
              )
            }
            error={Boolean(fieldErrors.DopplerCarotidian)}
            helperText={fieldErrors.DopplerCarotidian}
            inputProps={{ maxLength: STRING_LIMITS.DopplerCarotidian }}
            multiline
            rows={3}
            fullWidth
          />

          <TextField
            label="Holter TA"
            value={form.HolterTA}
            onChange={(e) => updateField("HolterTA", e.target.value)}
            onBlur={(e) =>
              validateStringField("HolterTA", "Holter TA", e.target.value)
            }
            error={Boolean(fieldErrors.HolterTA)}
            helperText={fieldErrors.HolterTA}
            inputProps={{ maxLength: STRING_LIMITS.HolterTA }}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="EKG"
            value={form.EKG}
            onChange={(e) => updateField("EKG", e.target.value)}
            onBlur={(e) => validateStringField("EKG", "EKG", e.target.value)}
            error={Boolean(fieldErrors.EKG)}
            helperText={fieldErrors.EKG}
            inputProps={{ maxLength: STRING_LIMITS.EKG }}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="Holter ECG"
            value={form.HolterECG}
            onChange={(e) => updateField("HolterECG", e.target.value)}
            onBlur={(e) =>
              validateStringField("HolterECG", "Holter ECG", e.target.value)
            }
            error={Boolean(fieldErrors.HolterECG)}
            helperText={fieldErrors.HolterECG}
            inputProps={{ maxLength: STRING_LIMITS.HolterECG }}
            multiline
            rows={3}
            fullWidth
          />

          <TextField
            label="Test de efort"
            value={form.TestEfort}
            onChange={(e) => updateField("TestEfort", e.target.value)}
            onBlur={(e) =>
              validateStringField("TestEfort", "Test de efort", e.target.value)
            }
            error={Boolean(fieldErrors.TestEfort)}
            helperText={fieldErrors.TestEfort}
            inputProps={{ maxLength: STRING_LIMITS.TestEfort }}
            multiline
            rows={4}
            fullWidth
          />
        </Stack>
      </TabPanel>

      {/* Tab 5: Treatment */}
      <TabPanel value={tabValue} index={4}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" color="primary" gutterBottom>
              💊 Tratament recomandat
            </Typography>
            <TextField
              label="Tratament recomandat"
              value={form.TratamentRecomandat || ""}
              onChange={(e) =>
                updateField("TratamentRecomandat", e.target.value)
              }
              onBlur={(e) =>
                validateStringField(
                  "TratamentRecomandat",
                  "Tratament recomandat",
                  e.target.value,
                )
              }
              error={Boolean(fieldErrors.TratamentRecomandat)}
              helperText={fieldErrors.TratamentRecomandat}
              inputProps={{ maxLength: STRING_LIMITS.TratamentRecomandat }}
              multiline
              rows={4}
              fullWidth
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6" color="primary" gutterBottom>
              📋 Tratament Efectuat
            </Typography>
            <TextField
              label="Tratament efectuat anterior"
              value={form.TratamentEfectuat || ""}
              onChange={(e) => updateField("TratamentEfectuat", e.target.value)}
              onBlur={(e) =>
                validateStringField(
                  "TratamentEfectuat",
                  "Tratament efectuat anterior",
                  e.target.value,
                )
              }
              error={Boolean(fieldErrors.TratamentEfectuat)}
              helperText={fieldErrors.TratamentEfectuat}
              inputProps={{ maxLength: STRING_LIMITS.TratamentEfectuat }}
              multiline
              rows={4}
              fullWidth
            />
          </Box>

          <Typography variant="body2" color="text.secondary">
            💡 Sfat: Adăugați toate medicamentele recomandate pacientului.
            Puteți specifica doza exactă, frecvența administrării și durata
            tratamentului.
          </Typography>
        </Stack>
      </TabPanel>

      {/* Tab 6: Prescription */}
      <TabPanel value={tabValue} index={5}>
        <Stack spacing={2}>
          <TextField
            label="Rețetă"
            value={form.Reteta}
            onChange={(e) => updateField("Reteta", e.target.value)}
            onBlur={(e) =>
              validateStringField("Reteta", "Rețetă", e.target.value)
            }
            error={Boolean(fieldErrors.Reteta)}
            helperText={fieldErrors.Reteta}
            inputProps={{ maxLength: STRING_LIMITS.Reteta }}
            multiline
            rows={8}
            fullWidth
          />
        </Stack>
      </TabPanel>

      {/* Action buttons - Always visible */}
      <Box sx={{ p: 3, borderTop: 1, borderColor: "divider" }}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await save();
              } catch (e) {
                // keep console noise minimal; errors are already logged
              }
            }}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              console.log("[ConsultationForm] Cancel clicked");
              console.log("[ConsultationForm] Current form state:", form);
            }}
          >
            Cancel
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
